import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { ConcentricRings } from '@/components/Motifs';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  })
};

export default function OracleDeckPage() {
  const [product, setProduct] = useState(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({ email: '', name: '' });
  usePageTitle();

  useEffect(() => {
    api.get('/products/oracle-deck').then(res => {
      setProduct(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const res = await api.post('/waitlist', { email });
      toast.success(res.data.message);
      setEmail('');
    } catch (err) {
      toast.error('Failed to join waitlist.');
    }
  };

  const handlePurchase = async () => {
    if (!checkoutForm.email) {
      toast.error('Please enter your email address.');
      return;
    }
    setPurchasing(true);
    try {
      const originUrl = window.location.origin;
      const res = await api.post('/checkout', {
        product_slug: 'oracle-deck',
        origin_url: originUrl,
        quantity: 1,
        customer_email: checkoutForm.email,
        customer_name: checkoutForm.name
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment processing error.');
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="flex justify-center py-32"><ConcentricRings size={60} opacity={0.3} /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* Hero */}
      <section className="hero-gradient relative py-20 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1608549785765-036974ba067d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85')` }} />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
          >
            <motion.p variants={fadeUp} className="font-['Inter'] text-xs font-medium uppercase tracking-[0.2em] text-[hsl(var(--gold))] mb-4">
              {product?.prelaunch ? 'Pre-Launch' : 'Available Now'}
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[hsl(var(--foreground))] mb-4">
              {product?.name || 'The Oracle Deck'}
            </motion.h1>
            <motion.p variants={fadeUp} className="font-['Cormorant_Garamond'] text-xl sm:text-2xl italic text-[hsl(var(--champagne))]">
              {product?.tagline}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.p variants={fadeUp} className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-6">
              About the Deck
            </motion.p>
            <motion.p variants={fadeUp} className="font-['Inter'] text-base sm:text-lg leading-[1.8] text-[hsl(var(--muted-foreground))] mb-8">
              {product?.description}
            </motion.p>
            <motion.p variants={fadeUp} className="font-['Inter'] text-[15px] leading-[1.8] text-[hsl(var(--muted-foreground))] mb-12">
              {product?.extended_description}
            </motion.p>

            {/* Features */}
            {product?.features?.length > 0 && (
              <motion.div variants={fadeUp}>
                <p className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-6">
                  What is Included
                </p>
                <ul className="space-y-4">
                  {product.features.map((feature, i) => (
                    <li key={feature} className="flex items-start gap-3">
                      <ArrowRight className="h-4 w-4 text-[hsl(var(--gold))] mt-1 shrink-0" />
                      <span className="font-['Inter'] text-[15px] leading-[1.7] text-[hsl(var(--muted-foreground))]">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Waitlist / Purchase */}
      <section className="py-16 sm:py-24 section-mist">
        <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            {product?.prelaunch ? (
              <>
                <motion.h2 variants={fadeUp} className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-4">
                  Join the Waitlist
                </motion.h2>
                <motion.p variants={fadeUp} className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] mb-8">
                  {product.prelaunch_message}
                </motion.p>
                <motion.form variants={fadeUp} onSubmit={handleWaitlist} className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Your email address"
                    className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:ring-[hsl(var(--gold))] font-['Inter'] text-sm"
                    data-testid="waitlist-email-input"
                    required
                  />
                  <Button
                    type="submit"
                    className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-8 rounded-lg hover:brightness-[1.03] shrink-0"
                    data-testid="waitlist-submit-button"
                  >
                    Join Waitlist
                  </Button>
                </motion.form>
              </>
            ) : (
              <>
                <motion.h2 variants={fadeUp} className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-4">
                  Acquire the Deck
                </motion.h2>
                <motion.p variants={fadeUp} className="font-['Cormorant_Garamond'] text-2xl text-[hsl(var(--gold))] mb-8">
                  ${product?.price?.toFixed(2)} {product?.currency}
                </motion.p>
                <motion.div variants={fadeUp} className="space-y-4 max-w-sm mx-auto">
                  <Input
                    type="text"
                    value={checkoutForm.name}
                    onChange={e => setCheckoutForm({...checkoutForm, name: e.target.value})}
                    placeholder="Your name"
                    className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
                  />
                  <Input
                    type="email"
                    value={checkoutForm.email}
                    onChange={e => setCheckoutForm({...checkoutForm, email: e.target.value})}
                    placeholder="Your email address"
                    className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
                    required
                  />
                  <Button
                    onClick={handlePurchase}
                    disabled={purchasing}
                    className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-12 py-6 rounded-lg hover:brightness-[1.03] disabled:opacity-50"
                  >
                    {purchasing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      'Purchase'
                    )}
                  </Button>
                </motion.div>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
